import { useState, useEffect } from 'react';
import { isIOS } from '../utils/browser';
import {
  getDeferredPrompt,
  clearDeferredPrompt,
  subscribe,
  getActuallyInstalled,
  refreshInstalledState,
  hasInstalledTicket,
  consumeInstalledTicket,
  isInstallModalConsumedThisSession,
  startInstalledPolling,
} from '../utils/pwaInstallCapture';

/**
 * PWA 설치 가능 여부 및 4단계 설치 상태(installState)를 관리하는 커스텀 훅
 * @returns {{
 *   canInstall: boolean,
 *   isInstalled: boolean,
 *   installState: 'ready' | 'installed' | 'unsupported' | 'unavailable',
 *   isIOS: boolean,
 *   justInstalled: boolean,
 *   clearJustInstalled: () => void,
 *   promptInstall: () => Promise<boolean>
 * }}
 */
export default function usePwaInstall() {
  const isIOSPlatform = isIOS();

  const [canInstall, setCanInstall] = useState(() => getDeferredPrompt() !== null);
  const [actuallyInstalled, setActuallyInstalled] = useState(() => getActuallyInstalled());
  const [justInstalled, setJustInstalled] = useState(
    () => hasInstalledTicket() && !isInstallModalConsumedThisSession()
  );

  // 1. 실제 설치 여부 및 4단계 installState 계산
  const isInstalled = actuallyInstalled === true;

  let installState = 'unavailable';
  if (canInstall) {
    installState = 'ready';
  } else if (isInstalled) {
    installState = 'installed';
  } else if (actuallyInstalled === null && isIOSPlatform) {
    installState = 'unsupported';
  } else {
    installState = 'unavailable';
  }

  useEffect(() => {
    // 2. 훅 마운트 시 실제 설치 상태 1회 최신화
    refreshInstalledState().catch(() => {});

    // 3. 전역 pwaInstallCapture 상태 변화 구독
    const unsubscribe = subscribe(() => {
      try {
        setCanInstall(getDeferredPrompt() !== null);
        setActuallyInstalled(getActuallyInstalled());
        setJustInstalled(hasInstalledTicket() && !isInstallModalConsumedThisSession());
      } catch {
        // 예외 방어
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // 4. 네이티브 설치 프롬프트 호출 함수
  const promptInstall = async () => {
    try {
      const promptEvent = getDeferredPrompt();
      if (!promptEvent || typeof promptEvent.prompt !== 'function') {
        return false;
      }

      await promptEvent.prompt();
      const choiceResult = await promptEvent.userChoice;

      clearDeferredPrompt();
      setCanInstall(false);

      const accepted = choiceResult?.outcome === 'accepted';

      // 사용자가 설치를 수락한 경우, 실제 설치 완료 확인까지 주기 조회 시작
      if (accepted) {
        startInstalledPolling();
      }

      await refreshInstalledState();

      return accepted;
    } catch {
      return false;
    }
  };

  // 5. 방금 설치 완료 상태 해제 함수 (티켓 소비 및 세션 도장 기록)
  const clearJustInstalled = () => {
    consumeInstalledTicket();
  };

  return {
    canInstall,
    isInstalled,
    installState,
    isIOS: isIOSPlatform,
    justInstalled,
    clearJustInstalled,
    promptInstall,
  };
}
